"use client";

/**
 * Rail panel: list of all cited-so-far chips with provenance dots.
 *
 * Extracted from `compass-intake.jsx:713-738` (`.cs-srclist`, `.cs-srcchip`).
 */

import { ProvenanceDot } from "../data-display/ProvenanceDot";
import { Plus } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";
import { PROV_META } from "../lib/provenance";

export interface SourceListCitation {
  id: number;
  value?: string;
  label: string;
  type: ProvenanceType;
}

export interface SourceListProps {
  /** Array of citations cited so far. */
  citations: SourceListCitation[];
  /** Edit overrides (keyed by id). */
  edits?: Record<number, boolean>;
  /** Callback when a citation chip is clicked. */
  onOpen?: (id: number) => void;
}

export function SourceList({ citations, edits, onOpen }: SourceListProps) {
  return (
    <div className="p-[13px] flex flex-col gap-2">
      <div className="text-[11px] tracking-[.1em] uppercase text-(--fg-muted) font-medium px-1 pt-[3px] pb-[5px]">
        Cited so far · {citations.length}
      </div>
      {citations.map((c) => {
        const isEdit = edits?.[c.id];
        const isEmpty = c.type === "empty" && !isEdit;
        return (
          <button
            key={c.id}
            type="button"
            className={[
              "flex items-center gap-[11px] w-full text-left",
              "bg-(--bg-white) border border-(--border-subtle)",
              "rounded-(--radius-field) px-[13px] py-[11px]",
              "cursor-pointer transition-[border-color,box-shadow] duration-150",
              "hover:border-(--border-strong) hover:shadow-(--shadow-xs)",
              "font-[family-name:var(--font-body)]",
              isEmpty ? "border-dashed" : "",
            ].join(" ")}
            onClick={() => onOpen?.(c.id)}
          >
            <span
              className={[
                "w-5 h-5 rounded-[5px] text-[11px] font-bold",
                "flex items-center justify-center shrink-0",
                isEmpty
                  ? "bg-(--empty-bg) border border-(--empty-border) text-(--empty)"
                  : "bg-(--bg-sky) border border-(--border-default) text-(--fg-brand)",
              ].join(" ")}
            >
              {c.id}
            </span>
            <ProvenanceDot type={isEdit ? "provided" : c.type} />
            <span className="flex-1 min-w-0 flex flex-col">
              <span className="text-[13px] font-semibold text-(--fg-primary) truncate">
                {c.label}
              </span>
              <span className="text-[11px] text-(--fg-muted) mt-px">
                {isEmpty
                  ? "Empty"
                  : isEdit
                    ? "Provided · you"
                    : c.type === "provided"
                      ? "Provided"
                      : (
                          PROV_META[c.type === "vault" ? "catalog" : c.type] ??
                          PROV_META.estimated
                        ).label}
              </span>
            </span>
            {isEmpty ? (
              <span className="text-(--empty) inline-flex">
                <Plus size={15} />
              </span>
            ) : (
              c.value && (
                <span className="text-[14px] font-bold text-(--fg-primary) shrink-0">
                  {c.value}
                </span>
              )
            )}
          </button>
        );
      })}
    </div>
  );
}
